import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

/** Default region when the user types a national number without +. */
const DEFAULT_REGION = 'RO';

/**
 * ISO 3166-1 alpha-2 → regional-indicator flag emoji (e.g. RO → 🇷🇴).
 *
 * @param {string} iso2
 * @returns {string}
 */
export function countryFlagEmoji(iso2) {
  const code = String(iso2 || '')
    .trim()
    .toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...[...code].map((c) => 127397 + c.charCodeAt(0)));
}

/**
 * True when the live query looks phone-like enough to format / show a flag.
 *
 * @param {unknown} raw
 * @returns {boolean}
 */
export function looksLikePhoneInput(raw) {
  const s = String(raw || '').trim();
  if (!s) return false;
  if (/[a-zA-Z@]/.test(s) && !/^\s*\+/.test(s)) return false;
  const digits = s.replace(/\D/g, '');
  return digits.length >= 3;
}

/**
 * Format as the user types (international grouping) and detect country.
 *
 * @param {unknown} raw
 * @param {{ defaultCountry?: string }} [opts]
 * @returns {{ formatted: string, flag: string, country: string, e164: string }}
 */
export function formatPhoneInputLive(raw, opts = {}) {
  const input = String(raw ?? '');
  const defaultCountry = opts.defaultCountry || DEFAULT_REGION;
  if (!looksLikePhoneInput(input)) {
    return { formatted: input, flag: '', country: '', e164: '' };
  }

  const typer = new AsYouType(defaultCountry);
  const formatted = typer.input(input);
  const country = String(typer.getCountry() || '').toUpperCase();
  const number = typer.getNumber();
  const e164 = number ? number.format('E.164') : '';
  return {
    formatted,
    flag: countryFlagEmoji(country),
    country,
    e164,
  };
}

/**
 * Normalize a phone query for search submission (prefer E.164).
 *
 * @param {unknown} raw
 * @param {{ defaultCountry?: string }} [opts]
 * @returns {string}
 */
export function normalizePhoneQueryForSubmit(raw, opts = {}) {
  const input = String(raw || '').trim();
  if (!input) return '';
  const defaultCountry = opts.defaultCountry || DEFAULT_REGION;
  const parsed = parsePhoneNumberFromString(input, defaultCountry);
  if (parsed?.isValid()) return parsed.format('E.164');
  const live = formatPhoneInputLive(input, { defaultCountry });
  return live.e164 || input;
}

/**
 * Classify telephony line kind for UI actions (WhatsApp gating).
 *
 * @param {object|null|undefined} res
 * @returns {'FIXED_LINE'|'MOBILE'|'VOIP'|'UNKNOWN'}
 */
export function resolvePhoneLineKind(res) {
  const details = res?.details && typeof res.details === 'object' ? res.details : {};
  const raw = String(details.line_type || details.number_type || res?.line_type || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (
    raw.includes('FIXED') ||
    raw.includes('LANDLINE') ||
    raw === 'FIXED_LINE' ||
    raw === 'FIXED_LINE_OR_MOBILE'
  ) {
    // FIXED_LINE_OR_MOBILE is ambiguous — prefer fixed when RO geographic prefix.
    if (raw === 'FIXED_LINE_OR_MOBILE') {
      const digits = String(details.e164 || res?.target || '').replace(/\D/g, '');
      const national = digits.startsWith('40') ? digits.slice(2) : digits;
      if (/^[23]/.test(national)) return 'FIXED_LINE';
      if (/^7/.test(national)) return 'MOBILE';
    } else {
      return 'FIXED_LINE';
    }
  }
  if (raw.includes('MOBILE') || raw.includes('CELL')) return 'MOBILE';
  if (raw.includes('VOIP')) return 'VOIP';

  const tagBlob = Array.isArray(res?.matched_tags)
    ? res.matched_tags.map((t) => String(t).toUpperCase()).join(' ')
    : '';
  if (/\bFIXED\b|\bLANDLINE\b|\bLINIE\s*FIX/.test(tagBlob)) return 'FIXED_LINE';
  if (/\bMOBILE\b|\bCELLULAR\b/.test(tagBlob)) return 'MOBILE';

  const digits = String(details.e164 || res?.target || '').replace(/\D/g, '');
  const national = digits.startsWith('40') ? digits.slice(2) : digits.replace(/^0/, '');
  if (/^[23]\d{8}$/.test(national)) return 'FIXED_LINE';
  if (/^7\d{8}$/.test(national)) return 'MOBILE';
  return 'UNKNOWN';
}

/**
 * Human location label for fixed-line prefix / metadata.
 *
 * @param {object|null|undefined} res
 * @returns {string}
 */
export function phoneLocationBadgeLabel(res) {
  const details = res?.details && typeof res.details === 'object' ? res.details : {};
  const parts = [
    details.city,
    details.region,
    details.location,
    details.geographic_area,
    details.area_name,
    details.country_name,
  ]
    .map((x) => String(x || '').trim())
    .filter(Boolean);
  const uniq = [];
  for (const p of parts) {
    if (!uniq.some((u) => u.toLowerCase() === p.toLowerCase())) uniq.push(p);
  }
  if (uniq.length) return uniq.slice(0, 2).join(', ');

  const locTag = (Array.isArray(res?.matched_tags) ? res.matched_tags : [])
    .map((t) => String(t || ''))
    .find((t) => /^Location:\s*/i.test(t));
  if (locTag) return locTag.replace(/^Location:\s*/i, '').trim();
  return '';
}

/**
 * E.164 string for copy / WhatsApp helpers.
 *
 * @param {object|null|undefined} res
 * @param {unknown} fallback
 * @returns {string}
 */
export function phoneE164FromResult(res, fallback = '') {
  const candidates = [
    res?.details?.e164,
    res?.target,
    res?.phone,
    fallback,
  ];
  for (const c of candidates) {
    const s = String(c || '').trim();
    if (!s) continue;
    const parsed = parsePhoneNumberFromString(s, DEFAULT_REGION);
    if (parsed?.isValid()) return parsed.format('E.164');
    if (s.startsWith('+') && s.replace(/\D/g, '').length >= 8) {
      return `+${s.replace(/\D/g, '')}`;
    }
  }
  return '';
}
